/**
 * MypageEditProfileContainer
 * --
 */
import React from 'react';
import MypageEditProfilePresenter from './MypageEditProfilePresenter';
import { useUserAction, useUserStack } from 'store';
import { useUpdateUser } from 'hooks/UserHooks';
import Formatter from 'utils/Formatter';
import { openAuthPopup } from 'utils/NiceAPIManager';
import MessageAlert, { DEPLOY_TYPE, DOMAIN_URL } from 'utils';
import { AuthApi } from 'api';
import { useDuplicateCheck } from 'hooks/AuthHooks';
import { useSendEmail } from 'hooks/SendHooks';
import validateCheck, { passwordCheck } from 'utils/Validation';

const MypageEditProfileContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, screenSize } = props;

  /* ===== Store ===== */
  const userInfo = useUserStack();
  const userAction = useUserAction();

  /* ===== Mutate ===== */
  const { updateUser } = useUpdateUser();
  // 중복검사
  const { duplicate } = useDuplicateCheck();
  // 이메일 인증
  const { sendEmail } = useSendEmail();

  /* ===== STATE ===== */
  const timerStart = React.useRef(null);

  const {
    user_account,
    user_name,
    user_phone,
    user_email,
    user_address,
    user_address_detail,
    user_setting,
    user_birthday,
    user_gender,
    postcode,
    social_id,
    social_type,
  } = userInfo;
  // 입력값
  const [inputValue, setInputValue] = React.useState({
    user_account,
    user_name,
    user_phone,
    user_email,
    user_address,
    user_address_detail,
    user_birthday,
    user_gender,
    postcode,
    social_id,
    social_type,
    year: Formatter.unixToDate(user_birthday, 'YYYY'),
    month: Formatter.unixToDate(user_birthday, 'MM'),
    day: Formatter.unixToDate(user_birthday, 'DD'),
    password: '',
    re_password: '',
  });
  // 동의 항목
  const [checkedList, setCheckedList] = React.useState({
    ...user_setting,
  });
  // 인증 진행중인지 체크
  const [isAuthProgress, setIsAuthProgress] = React.useState(false);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 이메일 인증번호
  const [inputAuthCode, setInputAuthCode] = React.useState('');
  const [authCode, setAuthCode] = React.useState('');
  const [timeText, setTimeText] = React.useState('');
  const [authStatus, setAuthStatus] = React.useState('');

  /* ===== VARIABLE ===== */
  // 체크박스 아이템
  const checkListItem = React.useMemo(() => {
    const items = [
      {
        key: 'terms_of_marketing',
        label: '마케팅 목적의 개인정보 수집 및 이용 동의',
        value: '마케팅 목적의 개인정보 수집 및 이용 동의',
        url: '',
      },
      {
        key: 'terms_of_ad',
        label: '혜택 및 광고성 정보 수신 동의',
        value: '혜택 및 광고성 정보 수신 동의',
        url: '',
      },
    ];

    return items;
  }, []);

  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩 함수
   * --
   */
  const handleChange = (t, v, callback = null) => {
    setInputValue((prevState) => {
      const newData = { ...prevState, [t]: v };

      if (callback) callback(v);

      return newData;
    });
  };

  /**
   * 체크박스 선택
   * --
   */
  const handleChecked = (t, c) => {
    setCheckedList((prev) => ({
      ...prev,
      [t]: c,
    }));
  };

  /**
   * 나이스 본인인증
   * --
   */
  const handleOpenAuthModal = () => {
    // 나이스 본인인증 팝업 열기
    openAuthPopup();
    // 나이스 본인인증 진행중 표시
    setIsAuthProgress(true);
  };

  /**
   * 본인인증 데이터 복호화
   * --
   */
  const handleEncodeData = async (encode) => {
    try {
      setIsLoading(true);

      const { status, data } = await AuthApi.verify({
        EncodeData: encodeURIComponent(encode),
      });

      if (status !== 200) {
        MessageAlert.warning('본인인증에 실패하였습니다');
        return;
      }

      const { name, gender, mobileno, birthdate } = data;

      const birthday = `${birthdate.substring(0, 4)}-${birthdate.substring(
        4,
        6,
      )}-${birthdate.substring(6)}`;
      const userBirthday = Formatter.dateToUnix(birthday);

      // NOTE: 기존 가입된 명의와 일치하는지 체크
      if (
        userInfo?.user_name !== name ||
        userInfo?.user_birthday !== userBirthday
      ) {
        MessageAlert.info('가입하신 정보와 일치하지 않습니다!');
        return;
      }

      const phoneValue = Formatter.hyphen(mobileno);

      const GENDER = {
        0: 'F',
        1: 'M',
      };

      handleChange('user_name', name);
      handleChange('user_phone', phoneValue);
      handleChange('user_gender', GENDER[gender]);
      handleChange('year', birthdate.slice(0, 4));
      handleChange('month', birthdate.slice(4, 6));
      handleChange('day', birthdate.slice(6, 8));

      MessageAlert.success('인증되었습니다.');
    } catch (err) {
      console.error('[handleEncodeData] Error!! : ', err);
      MessageAlert.error('본인인증에 실패하였습니다');
    } finally {
      setTimeout(() => {
        setIsAuthProgress(false); // 인증 진행 상태를 false로 설정
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 인증번호 전송
   * --
   */
  const handleSendAuthCode = async () => {
    const { user_email } = inputValue;

    if (!user_email) {
      MessageAlert.info('이메일을 입력해주세요!');
      return;
    }

    if (!validateCheck('email', user_email)) {
      MessageAlert.info('이메일 형식이 아닙니다!');
      return;
    }

    // 이메일 유효성 검사
    // if (!(await handleDuplicate('user_email', user_email))) {
    //   return;
    // }

    try {
      setIsLoading(true);

      const res = await duplicate({
        user_email: user_email,
        type: 'USER',
      });

      if (res.status !== 200 || !res.data) {
        MessageAlert.warning('사용할 수 없는 이메일입니다!');
        return;
      }

      // 인증번호 전송
      const bodyData = {
        to_email: user_email,
        type: 'CODE',
        user_type: 'USER',
        is_template: 'Y',
      };

      const { status, data } = await sendEmail(bodyData);

      if (status !== 200) {
        MessageAlert.warning('인증번호 전송에 실패하였습니다.');
        return;
      }

      setAuthCode(data.code);
      handleStartTimer();
    } catch (err) {
      console.log(`인증번호 전송 실패`, err);
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /**
   * 인증코드 입력 시간 제한 설정
   * --
   */
  const handleStartTimer = () => {
    if (timerStart.current) {
      clearInterval(timerStart.current);
      timerStart.current = null;
    }

    let time = 300;
    timerStart.current = setInterval(() => {
      --time;

      if (time === 0) {
        setAuthCode('만료');
        clearInterval(timerStart.current);
        timerStart.current = null;
        setTimeText('시간만료');
        return;
      }

      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      const formattedMinutes = String(minutes).padStart(2, '0');
      const formattedSeconds = String(seconds).padStart(2, '0');
      setTimeText(`${formattedMinutes}:${formattedSeconds}`);
    }, 1000);
  };

  /**
   * 인증코드 체크
   * --
   */
  const handleAuthCheck = () => {
    if (authCode === '만료') {
      MessageAlert.info('인증코드가 만료되었습니다. 다시 인증해주세요!');
      return;
    }

    if (!inputAuthCode || authCode !== inputAuthCode) {
      MessageAlert.info('인증번호가 올바르지 않습니다.');
      return;
    }

    setAuthStatus('success');
    setTimeText('');

    clearInterval(timerStart.current);
    timerStart.current = null;
    MessageAlert.success('인증되었습니다.');
  };

  /**
   * 회원정보 수정
   * --
   */
  const handleSubmit = async () => {
    const { password, re_password, user_email, ...rest } = inputValue;

    let isPassword = false,
      isEmail = false;

    // NOTE: 비밀번호를 변경할 경우
    if (password || re_password) {
      if (!password) {
        MessageAlert.info('비밀번호를 입력해주세요.');
        return;
      }

      if (!re_password) {
        MessageAlert.info('비밀번호를 한번 더 입력해주세요.');
        return;
      }

      if (password !== re_password) {
        MessageAlert.info('비밀번호가 일치하지 않습니다.');
        return;
      }

      if (!passwordCheck(password)) {
        return;
      }

      isPassword = true;
    }

    // NOTE: 이메일을 변경할 경우
    if (authStatus === 'success') {
      isEmail = true;
    }
    // 상세주소는 공백만 입력한 경우도 저장하지 않습니다.
    if (!rest.user_address_detail?.trim()) {
      MessageAlert.info('상세 주소를 입력해주세요!');
      return;
    }

    try {
      const updateData = {
        user_id: userInfo.user_id,
        ...rest,
        ...(isPassword && { password: password }),
        ...(isEmail && { user_email: user_email }),
        user_setting: {
          ...checkedList,
        },
      };

      const res = await updateUser(updateData);

      if (res.status !== 200) {
        MessageAlert.info('회원정보 수정에 실패하였습니다!');
        return;
      }

      userAction.setUser({
        ...userInfo,
        ...res.data,
      });

      MessageAlert.success('수정되었습니다!');
    } catch (err) {
      console.error('[handleSubmit] Error!! : ', err);
      MessageAlert.error('회원정보 수정에 실패하였습니다!');
    } finally {
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 본인인증 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    const port = DEPLOY_TYPE === 'local' ? ':3000' : '';

    const handleMessage = (event) => {
      const domain =
        DEPLOY_TYPE === 'development'
          ? `mall-jm-coffee.${DOMAIN_URL}`
          : `mall.${DOMAIN_URL}`;

      // 이벤트의 출처를 확인하여 신뢰할 수 있는 출처에서 온 메시지만 처리
      if (
        event.origin === `${window.location.protocol}//${DOMAIN_URL}${port}` ||
        event.origin === `${window.location.protocol}//${domain}${port}`
      ) {
        const data = event.data;

        // AUTH 타입의 메시지인 경우
        if (data && data.type === 'AUTH') {
          handleEncodeData(data.data);
        }
      }
    };

    // 인증 진행 중인 경우에만 메시지 리스너를 등록
    if (isAuthProgress) {
      window.addEventListener('message', handleMessage);
    } else {
      window.removeEventListener('message', handleMessage);
    }

    return () => {
      // 컴포넌트가 언마운트될 때 메시지 리스너를 제거
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, [isAuthProgress]);

  /* ===== RENDER ===== */
  return (
    <>
      <MypageEditProfilePresenter
        user={userInfo}
        inputValue={inputValue}
        navigate={navigate}
        checkListItem={checkListItem}
        checkedList={checkedList}
        onChange={handleChange}
        onChecked={handleChecked}
        isLoading={isLoading}
        onOpenAuthPopup={handleOpenAuthModal}
        authCode={authCode}
        timeText={timeText}
        authStatus={authStatus}
        inputAuthCode={inputAuthCode}
        onSendAuthCode={handleSendAuthCode}
        onAuthCode={handleAuthCheck}
        setInputAuthCode={setInputAuthCode}
        onSubmit={handleSubmit}
        screenSize={screenSize}
      />
    </>
  );
};

export default MypageEditProfileContainer;
